import React from 'react';

import { Badge } from '@/design-system/badge';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import type {
  HubOverviewRow,
  HubOverviewVelocityCell,
} from '@/services/api/dashboard';

import {
  fmtInt,
  fmtPct,
  fmtSecPerItem,
  fmtTrendDelta,
  trendTone,
  VOLUME_STRESS_LABELS,
  VOLUME_STRESS_STATUS,
} from './format';

const TREND_CLS: Record<'up' | 'down' | 'flat', string> = {
  up: 'text-success-base',
  down: 'text-error-base',
  flat: 'text-text-soft-400',
};

const TREND_ICON: Record<'up' | 'down' | 'flat', string> = {
  up: 'ri-arrow-right-up-line',
  down: 'ri-arrow-right-down-line',
  flat: 'ri-subtract-line',
};

/**
 * Velocity cell: MA sec/item with a trend arrow. Standing decision — the
 * comparison number is velocity, never items/hour; the raw arrow direction
 * is recolored so "faster" always reads green.
 */
const VelocityCell: React.FC<{ cell: HubOverviewVelocityCell }> = ({
  cell,
}) => {
  if (cell.status !== 'OK' || cell.value == null) {
    return (
      <span className="text-[11px] text-text-soft-400">
        {cell.status === 'UNAVAILABLE' ? 'Unavailable' : 'Thin data'}
      </span>
    );
  }
  const tone = trendTone(cell.trend.direction);
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="font-mono text-[13px] font-semibold tabular-nums text-text-strong-950">
        {fmtSecPerItem(cell.value)}
      </span>
      <span
        className={`inline-flex items-center gap-0.5 text-[11px] tabular-nums ${TREND_CLS[tone]}`}
      >
        <i className={`${TREND_ICON[cell.trend.direction]} text-[12px]`} />
        {fmtTrendDelta(cell.trend)}
      </span>
    </div>
  );
};

interface OverviewTableProps {
  rows: HubOverviewRow[];
  loading: boolean;
  onSelectHub: (hubId: number) => void;
}

const OverviewTable: React.FC<OverviewTableProps> = ({
  rows,
  loading,
  onSelectHub,
}) => {
  const columns: TableDataColumn<HubOverviewRow>[] = [
    {
      key: 'hubName',
      title: 'Hub',
      render: (_v, r) => (
        <div>
          <p className="text-[13px] font-semibold tracking-tight text-text-strong-950">
            {r.hubName}
          </p>
          {r.lowCoverage && (
            <p className="mt-0.5 text-[10px] text-warning-base">
              Low coverage · {fmtPct(r.coverage)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'inboundVelocity',
      title: 'Inbound',
      render: (_v, r) => <VelocityCell cell={r.inboundVelocity} />,
    },
    {
      key: 'dispatchVelocity',
      title: 'Dispatch',
      render: (_v, r) => <VelocityCell cell={r.dispatchVelocity} />,
    },
    {
      key: 'riderScanVelocity',
      title: 'Rider scan',
      render: (_v, r) => <VelocityCell cell={r.riderScanVelocity} />,
    },
    {
      key: 'scannedCount',
      title: 'Volume',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
          {fmtInt(r.scannedCount)} pkgs
        </span>
      ),
    },
    {
      key: 'plannerStress',
      title: 'Planner stress',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
          {fmtInt(r.plannerStress)}
          {r.plannerStressMa != null && (
            <span className="text-text-soft-400">
              {' '}
              · MA {r.plannerStressMa.toFixed(1)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'volumeStress',
      title: 'Volume stress',
      render: (_v, r) => {
        const vs = r.volumeStress;
        if (vs.band == null) {
          return (
            <span className="text-[11px] text-text-soft-400">
              {vs.status === 'INSUFFICIENT_HISTORY'
                ? 'Short history'
                : '—'}
            </span>
          );
        }
        return (
          <div className="flex items-center gap-1.5">
            <Badge status={VOLUME_STRESS_STATUS[vs.band]} type="dot">
              {VOLUME_STRESS_LABELS[vs.band]}
            </Badge>
            {vs.shortHistory && (
              <span
                className="text-[10px] text-text-soft-400"
                title="14–29 days of history back this baseline"
              >
                short hist.
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <TableData<HubOverviewRow>
      columns={columns}
      data={rows}
      loading={loading}
      onRowClick={(r) => onSelectHub(r.hubId)}
      emptyState="No hubs with snapshot data in this window"
    />
  );
};

export default OverviewTable;
